import React, { useState, useEffect, useRef } from "react";
import { MapPin, RefreshCw, Search } from "lucide-react";
import { API_BASE_URL, API_ENDPOINTS } from "../constants";
import TrackingMap from "./tracking/TrackingMap";
import VehicleList from "./tracking/VehicleList";
import StatsCards from "./tracking/StatsCards";
import PageHeader from "./tracking/PageHeader";
import QuickViewPanel from "./tracking/QuickViewPanel";

const TrackingPage = () => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [hoveredVehicle, setHoveredVehicle] = useState(null);
  const [showTrafficLayer, setShowTrafficLayer] = useState(true);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [filters, setFilters] = useState({
    status: "all",
    search: "",
  });
  const [vehicles, setVehicles] = useState([]);
  const mapInitialized = useRef(false);

  useEffect(() => {
    get_active_trip();
    const interval = setInterval(() => {
      get_active_trip();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const get_active_trip = async () => {
    try {
      setLoading(vehicles.length === 0);
      const url = new URL(API_BASE_URL + API_ENDPOINTS.ALLACTIVETRIPLOCATION);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      let res = await response.json();
      console.log("Active Jobs Response:", res.data);

      let activeTrip = res.data.map((trip) => {
        const hasLocation = trip?.terminalLocation?.currentLocation;
        const hasValidCoords =
          hasLocation &&
          trip.terminalLocation.currentLocation.latitude &&
          trip.terminalLocation.currentLocation.longitude;

        return {
          id: trip.tripName || `device-${trip.deviceName}`,
          number: trip.tripName ? trip.tripName.split("-")[0] : "N/A",
          driver: trip.deviceName || "Unknown Driver",
          phone: trip?.terminalLocation?.address || "N/A",
          jobId: trip.tripName || "N/A",
          status: trip.tripStatus?.toLowerCase() || "unknown",
          position: {
            lat: hasValidCoords
              ? parseFloat(trip.terminalLocation.currentLocation.latitude)
              : 20.5937,
            lng: hasValidCoords
              ? parseFloat(trip.terminalLocation.currentLocation.longitude)
              : 78.9629,
            address:
              trip?.terminalLocation?.currentLocation?.detailedAddress ||
              "Location not available",
          },
          speed: trip.averageSpeed || 0,
          heading: "-",
          lastUpdate: trip?.terminalLocation?.currentLocation?.timestamp
            ? new Date(trip.terminalLocation.currentLocation.timestamp)
            : new Date(),
          lastTrackingTime: trip.lastTrackingTime || "N/A",
          lastTrackingDate: trip.lastTrackingDate || "N/A",
          route: {
            origin: "N/A",
            destination: "N/A",
            progress: 0,
            eta: "N/A",
            distance: {
              total: 0,
              completed: trip.traveledDistance || 0,
              remaining: 0,
            },
            polyline: [],
          },
          vehicle: {
            type: "Truck",
            model: "Commercial Vehicle",
            fuelLevel: 0,
            engineTemp: 0,
            batteryLevel: 0,
          },
          alerts: [],
          container: "N/A",
          customer: "N/A",
          trackingLink: trip.trackingLinkMap || null,
          stopTime: trip.stopTime || null,
          locationStatus:
            trip?.terminalLocation?.locationRetrievalStatus || "Unknown",
          isTracked: trip?.terminalLocation?.tracked || false,
        };
      });

      setVehicles(activeTrip);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Error fetching active trips:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVehicles = vehicles.filter((vehicle) => {
    if (filters.status !== "all" && vehicle.status !== filters.status)
      return false;
    if (
      filters.search &&
      !vehicle.number.toLowerCase().includes(filters.search.toLowerCase()) &&
      !vehicle.driver.toLowerCase().includes(filters.search.toLowerCase())
    )
      return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <PageHeader
          lastRefresh={lastRefresh}
          filters={filters}
          setFilters={setFilters}
          onRefresh={get_active_trip}
          loading={loading}
        />

        {/* Quick Stats */}
        <StatsCards vehicles={vehicles} />

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Map */}
          <div className="xl:col-span-9">
            <TrackingMap
              vehicles={filteredVehicles}
              selectedVehicle={selectedVehicle}
              hoveredVehicle={hoveredVehicle}
              setHoveredVehicle={setHoveredVehicle}
              setSelectedVehicle={setSelectedVehicle}
              showTrafficLayer={showTrafficLayer}
              setShowTrafficLayer={setShowTrafficLayer}
              mapInitialized={mapInitialized}
            />
          </div>

          {/* Vehicle List */}
          <div className="xl:col-span-3">
            <VehicleList
              vehicles={filteredVehicles}
              selectedVehicle={selectedVehicle}
              setSelectedVehicle={setSelectedVehicle}
              setHoveredVehicle={setHoveredVehicle}
              loading={loading}
            />
          </div>
        </div>

        {/* Quick View Panel (on hover) */}
        <QuickViewPanel vehicle={hoveredVehicle} />

        {/* Loading Overlay */}
        {loading && vehicles.length === 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity duration-300">
            <div className="bg-white rounded-2xl p-8 shadow-2xl transform animate-scale-in">
              <div className="flex items-center space-x-4">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Loading Fleet Data
                  </h3>
                  <p className="text-sm text-gray-600">Please wait...</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TrackingPage;
